/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import Lottie from "lottie-react";
import moment from "moment";
import { BiSolidEdit } from "react-icons/bi";
import { BsTrash3Fill } from "react-icons/bs";
import { FiDownload } from "react-icons/fi";
import { PiEyeDuotone, PiLinkDuotone } from "react-icons/pi";
import { RxCross2 } from "react-icons/rx";
import { TbTrash } from "react-icons/tb";
import { Button, Col, Row, Table, Tooltip } from "reactstrap";
import {
  applyClassToCharacters,
  CheckPermission,
  digitCount,
  LoadingSpinner,
  removeDuplicates,
} from "../../../constants/const";
import { GetLanguageString, NoResult } from "../../helper/Components";
import { GetSelectedLanguage } from "../../helper/Method";
import DetailBox from "../DetailBox";
import UploadStatus from "../UploadStatus";
import downloadingAnimation from "../downloadingIconAnimation.json";
import "./DocumentTables.css";

const DocumentTable = ({
  state,
  t,
  handlers,
  openFolder,
  toggleTooltip,
  formatSize,
  getFileIcon,
  getUnitName,
  dropzoneHandlers,
  renderButtons,
}) => {
  useEffect(() => {
    const handleMouseUp = () => {
      if (state.dragTimeout) {
        clearTimeout(state.dragTimeout);
        state.setDragTimeout(null);
      }
      state.setSelectionBox({ start: null, current: null });
    };

    const handleMouseMove = (e) => {
      if (state.selectionBox.start) {
        state.setSelectionBox((prev) => ({
          ...prev,
          current: { x: e.clientX, y: e.clientY },
        }));
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [state.dragTimeout, state.selectionBox.start]);

  useEffect(() => {
    if (
      state.selectionBox.start &&
      state.selectionBox.current &&
      !state.loading &&
      state.documents.length > 0 &&
      state.tableRef.current
    ) {
      const tableRect = state.tableRef.current.getBoundingClientRect();
      const startY =
        Math.min(state.selectionBox.start.y, state.selectionBox.current.y) -
        tableRect.top;
      const endY =
        Math.max(state.selectionBox.start.y, state.selectionBox.current.y) -
        tableRect.top;

      const newSelectedRows = [];
      state.documents.forEach((doc, index) => {
        const rowElement = state.tableRef.current.querySelector(
          `tbody tr:nth-child(${index + 1})`
        );

        if (!rowElement) return;

        const rowRect = rowElement.getBoundingClientRect();
        const rowTop = rowRect.top - tableRect.top;
        const rowBottom = rowRect.bottom - tableRect.top;

        if (
          (rowTop >= startY && rowTop <= endY) ||
          (rowBottom >= startY && rowBottom <= endY)
        ) {
          newSelectedRows.push(doc);
        }
      });

      state.setSelectedByDragging(true);
      state.setSelectedDocumentsForDelete(newSelectedRows);
    }
  }, [state.selectionBox, state.documents, state.loading]);

  const handleMouseDown = (e) => {
    const timeout = setTimeout(() => {
      state.setSelectionBox({
        start: { x: e.clientX, y: e.clientY },
        current: { x: e.clientX, y: e.clientY },
      });
      state.setSelectedDocumentsForDelete([]);
      state.setSelectedByDragging(false);
    }, 200);

    state.setDragTimeout(timeout);
  };

  const clearBulkSelection = () => {
    state.setSelectedDocumentsForDelete([]);
    state.setSelectedByDragging(false);
    state.setSelectedRowIndex(null);
    state.setHoveredRowIndex(null);
  };

  const handleRowClick = (e, index, file) => {
    if (
      state.selectedDocumentsForDelete.length > 0 &&
      e.ctrlKey &&
      state.selectedByDragging
    ) {
      handlers.removeIdFromSelectedRows(file.id);
      state.setHoveredRowIndex(null);
      state.setSelectedRowIndex(null);
      state.setSelectedFile(null);
      state.setDetailBox(false);
      return;
    }

    if (e.ctrlKey) {
      if (file.canChangePermissions) {
        if (
          !state.selectedDocumentsForDelete.some(
            (previousRow) => file.id === previousRow.id
          )
        ) {
          state.setSelectedDocumentsForDelete((prevSelectedRows) => {
            let newSelection = [...prevSelectedRows];

            if (
              state.selectedFile &&
              state.selectedFile.canChangePermissions &&
              !newSelection.some(
                (prevRow) => prevRow.id === state.selectedFile.id
              )
            ) {
              newSelection.push(state.selectedFile);
            }

            if (
              !state.selectedFile &&
              state.selectedRowIndex !== null &&
              state.documents[state.selectedRowIndex] &&
              state.documents[state.selectedRowIndex].canChangePermissions &&
              !newSelection.some(
                (prevRow) =>
                  prevRow.id === state.documents[state.selectedRowIndex].id
              )
            ) {
              newSelection.push(state.documents[state.selectedRowIndex]);
            }

            newSelection.push(file);
            return removeDuplicates(newSelection);
          });
          state.setSelectedFile(null);
        } else {
          state.setSelectedDocumentsForDelete((prevSelectedRows) => {
            const rowExists = prevSelectedRows.some(
              (selectedRow) => selectedRow.id === file.id
            );

            if (rowExists) {
              return removeDuplicates(
                prevSelectedRows.filter(
                  (selectedRow) => selectedRow.id !== file.id
                )
              );
            }

            return removeDuplicates([...prevSelectedRows, file]);
          });
        }
      }

      state.setSelectedRowIndex(null);
      state.setHoveredRowIndex(null);
      state.setDetailBox(false);
      return;
    }

    if (!e.target.closest(".unclickable-column")) {
      state.setSelectedRowIndex(
        index === state.selectedRowIndex ? null : index
      );
      state.setSelectedDocumentsForDelete([]);
      state.setSelectedByDragging(false);
    }

    if (
      !file.fileType.includes("folder") &&
      !e.target.closest(".unclickable-column") &&
      state.selectedDocumentsForDelete.length === 0 &&
      !e.ctrlKey
    ) {
      state.setSelectedFile(file);
      state.setSelectedDocumentsForDelete([]);
      state.setSelectedByDragging(false);
      state.setDetailBox(true);
    } else {
      state.setDetailBox(false);
    }
  };

  const renderBulkDeleteBar = () => {
    if (
      !(state.selectedDocumentsForDelete.length > 0 || state.selectedByDragging) ||
      state.deletionInProgress
    ) {
      return null;
    }

    return (
      <Row
        style={{
          borderRadius: "25px",
          border: "1px solid #cccccc",
          padding: 3,
          margin: 3,
          marginBottom: 20,
          marginTop: -10,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Col md="1" style={{ width: "40px" }}>
          <RxCross2
            color="gray"
            size={25}
            style={{ marginTop: "7px", cursor: "pointer" }}
            onClick={clearBulkSelection}
          />
        </Col>
        <Col md="7">
          <div style={{ marginTop: "6px", cursor: "default" }}>
            {state.selectedDocumentsForDelete.length}
            &nbsp;
            <GetLanguageString
              props={
                digitCount(state.selectedDocumentsForDelete.length) > 1
                  ? "document_files_selected"
                  : "document_file_selected"
              }
            />
          </div>
        </Col>
        <Col md="4" className="text-right">
          <Button
            color="default"
            id="deleteMultipleButtonTooltip"
            style={{ borderRadius: 20 }}
            onClick={handlers.handleMultipleDelete}
          >
            <TbTrash size={16} color="#D9534F" />
            &nbsp;
            {state.selectedDocumentsForDelete.length}
            &nbsp;
            <GetLanguageString props={"common_delete"} />
          </Button>
          <Tooltip
            isOpen={state.deleteMultipleButtonTooltipOpen}
            target="deleteMultipleButtonTooltip"
            toggle={() =>
              state.setDeleteMultipleButtonTooltipOpen(
                !state.deleteMultipleButtonTooltipOpen
              )
            }
          >
            <GetLanguageString props={"common_delete"} />
          </Tooltip>
        </Col>
      </Row>
    );
  };

  const renderDropOverlay = () => {
    if (!dropzoneHandlers.isDragActive) return null;

    const isInvalidTarget =
      state.folderParentUnitIds.includes(state.unitFolderUnitId) ||
      state.resultFromSearch;

    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isInvalidTarget
            ? "rgba(250, 197, 201, 0.8)"
            : "rgba(255, 255, 255, 0.8)",
          border: "2px dashed #cccccc",
          borderRadius: "4px",
          cursor: isInvalidTarget ? "not-allowed" : "default",
        }}
      >
        <p style={{ fontSize: "24px", color: "#333" }}>
          {isInvalidTarget ? (
            <GetLanguageString props={"file_cannot_be_dropped_here"} />
          ) : (
            <GetLanguageString props={"drop_files_here_to_upload"} />
          )}
        </p>
      </div>
    );
  };

  const renderSelectionBox = () => {
    if (!(state.selectionBox.start && state.selectionBox.current)) return null;

    return (
      <div
        style={{
          position: "fixed",
          border: "1px solid #9ce5cd",
          backgroundColor: "rgba(156, 229, 205, 0.2)",
          left: Math.min(
            state.selectionBox.start.x,
            state.selectionBox.current.x
          ),
          top: Math.min(
            state.selectionBox.start.y,
            state.selectionBox.current.y
          ),
          width: Math.abs(
            state.selectionBox.current.x - state.selectionBox.start.x
          ),
          height: Math.abs(
            state.selectionBox.current.y - state.selectionBox.start.y
          ),
          pointerEvents: "none",
        }}
      />
    );
  };

  if (state.loading) {
    return <div className="panel-body">{LoadingSpinner()}</div>;
  }

  if (state.documents.length === 0) {
    return (
      <div className="panel-body" style={{ userSelect: "none", position: "relative" }}>
        <div {...dropzoneHandlers.getRootProps()}>
          <input {...dropzoneHandlers.getInputProps()} />
          <div
            className="text-center"
            style={{
              fontStyle: "italic",
              color: "#FF3333",
              marginTop: 30,
              marginBottom: 10,
            }}
          >
            <h4>
              {state.resultFromSearch ? (
                <NoResult />
              ) : (
                <GetLanguageString
                  props={
                    CheckPermission("CanAddDocuments") &&
                    !state.folderParentUnitIds.includes(state.unitFolderUnitId)
                      ? "document_no_file_folder_present"
                      : "document_no_file_folder_present_only"
                  }
                />
              )}
            </h4>
          </div>
          <div className="text-center" style={{ marginBottom: 30 }}>
            {state.folderParentUnitIds.includes(state.unitFolderUnitId) ||
            state.resultFromSearch
              ? null
              : renderButtons?.()}
          </div>
          {renderDropOverlay()}
        </div>
      </div>
    );
  }

  return (
    <div
      className="panel-body"
      style={{ userSelect: "none", position: "relative" }}
      onMouseDown={handleMouseDown}
    >
      <div
        {...dropzoneHandlers.getRootProps()}
        style={{ outline: "none", border: "none", boxShadow: "none" }}
      >
        <input {...dropzoneHandlers.getInputProps()} />

        {(state.documents.length !== 0 || state.unitFolders.length !== 0) &&
        !state.folderParentUnitIds.includes(state.unitFolderUnitId) &&
        !state.resultFromSearch ? (
          <div style={{ height: 35, marginBottom: 20, marginTop: -10 }}>
            {renderBulkDeleteBar()}
          </div>
        ) : null}

        <Row>
          <Col md={state.detailBox ? "9" : "12"}>
            <Table responsive hover bordered innerRef={state.tableRef}>
              <thead style={{ cursor: "default", userSelect: "none" }}>
                <tr>
                  <th>#</th>
                  <th>
                    <GetLanguageString props={"common_name"} />
                  </th>
                  <th>
                    <GetLanguageString props={"common_description"} />
                  </th>
                  <th>
                    <GetLanguageString
                      props={"member_member_detail_document_upload_date"}
                    />
                  </th>
                  {state.resultFromSearch ? (
                    <th>
                      <GetLanguageString props={"folder_path"} />
                    </th>
                  ) : null}
                  <th>
                    <GetLanguageString
                      props={"member_member_detail_document_size"}
                    />
                  </th>
                  <th>
                    <GetLanguageString props={"dashboard_event_page_status"} />
                  </th>
                  <th className="unclickable-column"></th>
                </tr>
              </thead>
              <tbody>
                {state.documents.map((file, index) => {
                  const rowActive =
                    state.selectedRowIndex === index ||
                    state.selectedDocumentsForDelete.some(
                      (item) => item.id === file.id
                    );
                  const canDownload =
                    CheckPermission("CanDownloadDocuments") &&
                    file.folderUnitId === null;
                  const canEdit =
                    ((CheckPermission("CanEditDocuments") &&
                      state.folderUnitId === state.loggedInUnitId) ||
                      file.canChangePermissions) &&
                    file.folderUnitId === null;
                  const canDelete =
                    ((CheckPermission("CanDeleteDocuments") &&
                      state.folderUnitId === state.loggedInUnitId) ||
                      file.canChangePermissions) &&
                    file.folderUnitId === null;
                  const canViewOrCopy =
                    file.fileStatus === "file_uploaded" ||
                    file.fileStatus === "link_added";
                  const canDownloadFile = file.fileStatus === "file_uploaded";
                  const isFileRow = !file.fileType.includes("folder");
                  const showCopyIcon =
                    isFileRow && canDownload && !state.isDownloading[index];
                  const showViewIcon =
                    isFileRow && canDownload && !state.isDownloading[index];
                  const showDownloadIcon =
                    isFileRow && canDownload && !state.isDownloading[index];
                  const showEditIcon =
                    canEdit && !state.isDownloading[index];
                  const showDeleteIcon =
                    canDelete && !state.isDownloading[index];

                  return (
                    <tr
                      key={file.id || index}
                      className={
                        rowActive
                          ? "documents-table-row is-selected"
                          : "documents-table-row"
                      }
                      onClick={(e) => handleRowClick(e, index, file)}
                      onDoubleClick={(e) => {
                        if (
                          !e.target.closest(".unclickable-column") &&
                          file.fileType.includes("folder") &&
                          file.id !== 0 &&
                          file.id !== undefined &&
                          typeof file.id !== "string" &&
                          !state.deletionInProgress
                        ) {
                          openFolder(file);
                        }
                      }}
                      style={{ cursor: "default" }}
                    >
                      <td>{index + 1}</td>
                      <td style={{ userSelect: "none" }}>
                        {getFileIcon(file.fileType || "")}
                        &nbsp;
                        {applyClassToCharacters(file.name, "ur")}
                        &nbsp;
                        {file.isUnitDeleted && (
                          <GetLanguageString props={"folder_unit_deleted"} />
                        )}
                      </td>
                      <td>{applyClassToCharacters(file.description, "ur")}</td>
                      <td>
                        {file.createdDate ? (
                          <>
                            {moment(file.createdDate)
                              .locale(GetSelectedLanguage())
                              .format("DD/MM/YYYY")}
                            &nbsp;
                            {GetSelectedLanguage() === "ur"
                              ? moment(file.createdDate)
                                  .locale(GetSelectedLanguage())
                                  .format("a h:mm")
                              : moment(file.createdDate)
                                  .locale(GetSelectedLanguage())
                                  .format("h:mm a")}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      {state.resultFromSearch ? (
                        <td>{getUnitName(file.folderPath)}</td>
                      ) : null}
                      <td>{file.size ? formatSize(file.size) : "-"}</td>
                      <td>
                        <UploadStatus
                          fileStatus={file.fileStatus}
                          jobId={file.fileUploadBackgroundJobId}
                          uploadRefreshDocuments={handlers.uploadRefreshDocuments}
                          fileId={file.id}
                        />
                      </td>
                      <td
                        className="text-center unclickable-column"
                        style={{ width: "150px" }}
                      >
                        <div>
                          {isFileRow ? (
                            <>
                              {showCopyIcon ? (
                                <PiLinkDuotone
                                  className={
                                    canViewOrCopy
                                      ? "row-action row-action-interactive"
                                      : "row-action"
                                  }
                                  size={20}
                                  id={`copyLink-${index}`}
                                  style={{
                                    outline: "none",
                                    cursor: canViewOrCopy ? "pointer" : "default",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (canViewOrCopy) {
                                      handlers.copyToClipboard(file.fileUrl);
                                    }
                                  }}
                                />
                              ) : null}
                              &nbsp;&nbsp;
                              {showViewIcon ? (
                                <PiEyeDuotone
                                  className={
                                    canViewOrCopy
                                      ? "row-action row-action-interactive"
                                      : "row-action"
                                  }
                                  size={20}
                                  id={`viewIcon-${index}`}
                                  style={{
                                    outline: "none",
                                    cursor: canViewOrCopy ? "pointer" : "default",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (canViewOrCopy) {
                                      window.open(file.fileUrl, "_blank");
                                    }
                                  }}
                                />
                              ) : null}
                              &nbsp;&nbsp;
                              {canDownload ? (
                                showDownloadIcon ? (
                                  <FiDownload
                                    className={
                                      canDownloadFile
                                        ? "row-action row-action-interactive"
                                        : "row-action"
                                    }
                                    size={18}
                                    id={`downloadIcon-${index}`}
                                    style={{
                                      outline: "none",
                                      cursor: canDownloadFile
                                        ? "pointer"
                                        : "default",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (canDownloadFile) {
                                        handlers.handleDownload(
                                          file.driveId,
                                          index
                                        );
                                      }
                                    }}
                                  />
                                ) : (
                                  <Lottie
                                    id={`downloadIcon-${index}`}
                                    animationData={downloadingAnimation}
                                    loop
                                    autoplay
                                    style={{
                                      width: "38px",
                                      height: "38px",
                                      marginBottom: -87,
                                      marginTop: -30,
                                      marginLeft: 47,
                                    }}
                                  />
                                )
                              ) : null}
                              &nbsp;&nbsp;
                            </>
                          ) : null}

                          {showEditIcon ? (
                            <BiSolidEdit
                              className="row-action row-action-interactive"
                              size={20}
                              id={`editIcon-${index}`}
                              style={{ outline: "none", cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                state.setEditValue(file.id);
                                if (file.fileType.includes("folder")) {
                                  state.setEditFolderModal(true);
                                } else {
                                  state.setEditFileModal(true);
                                }
                              }}
                            />
                          ) : null}
                          &nbsp;&nbsp;
                          {showDeleteIcon ? (
                            <BsTrash3Fill
                              className="row-action row-action-interactive"
                              id={`deleteIcon-${index}`}
                              style={{ outline: "none", cursor: "pointer" }}
                              size={17}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlers.handleOnDelete(
                                  file.id,
                                  file.fileType,
                                  file.folderHaveFilesOrFolders
                                );
                              }}
                            />
                          ) : null}

                          {showDownloadIcon ? (
                            <Tooltip
                              isOpen={
                                state.tooltipOpen[`download-${index}`] || false
                              }
                              target={`downloadIcon-${index}`}
                              toggle={() => toggleTooltip("download", index)}
                            >
                              <GetLanguageString
                                props={
                                  "member_add_rafiq_request_download_document"
                                }
                              />
                            </Tooltip>
                          ) : null}

                          {showEditIcon ? (
                            <Tooltip
                              isOpen={state.tooltipOpen[`edit-${index}`] || false}
                              target={`editIcon-${index}`}
                              toggle={() => toggleTooltip("edit", index)}
                            >
                              <GetLanguageString props={"common_edit"} />
                            </Tooltip>
                          ) : null}

                          {showViewIcon ? (
                            <Tooltip
                              isOpen={state.tooltipOpen[`view-${index}`] || false}
                              target={`viewIcon-${index}`}
                              toggle={() => toggleTooltip("view", index)}
                            >
                              <GetLanguageString props={"member_view"} />
                            </Tooltip>
                          ) : null}

                          {showCopyIcon ? (
                            <Tooltip
                              isOpen={state.tooltipOpen[`copy-${index}`] || false}
                              target={`copyLink-${index}`}
                              toggle={() => toggleTooltip("copy", index)}
                            >
                              <GetLanguageString props={"document_link_copy"} />
                            </Tooltip>
                          ) : null}

                          {showDeleteIcon ? (
                            <Tooltip
                              isOpen={
                                state.tooltipOpen[`delete-${index}`] || false
                              }
                              target={`deleteIcon-${index}`}
                              toggle={() => toggleTooltip("delete", index)}
                            >
                              <GetLanguageString props={"common_delete"} />
                            </Tooltip>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Col>
          <DetailBox
            detailBox={state.detailBox}
            setDetailBox={state.setDetailBox}
            selectedFile={state.selectedFile}
            formatSize={formatSize}
            getUnitName={getUnitName}
          />
        </Row>

        {renderSelectionBox()}
        {renderDropOverlay()}
      </div>
    </div>
  );
};

export default DocumentTable;
